import React from 'react';
import { Box, Dialog, DialogContent, Typography, Alert } from '@mui/material';
import CustomButton from 'lib/designSystem/Button';
import RiskPremiumCalculator from './RiskPremiumCalculator';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'services/redux/store';
import { getCreditLineByProduct } from 'lib/api/creditLine';
import { getUser } from 'lib/api';
import { useEffect, useState } from 'react';
import WarrantyDetailTabs from './WarrantyDetailTabs';
import { Warning } from '@mui/icons-material';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { SGR_TYPES } from 'lib/consts';

interface WarrantyDetailDialogProps {
  open: boolean;
  onClose: () => void;
  warranty: any;
  onDelete: () => void;
  onRequest: () => void;
}

const WarrantyDetailDialog: React.FC<WarrantyDetailDialogProps> = ({
  open,
  onClose,
  warranty,
  onDelete,
  onRequest,
}) => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const [creditLineData, setCreditLineData] = useState<any>(null);
  const [isLoadingCreditLine, setIsLoadingCreditLine] = useState(false);

  const fetchCreditLineData = async () => {
    try {
      setIsLoadingCreditLine(true);
      const profile = await getUser(dispatch, { id: auth.user?.id });
      if (!profile) throw new Error('Profile not found');
      const businessIdentifier = profile.company.taxIdentifier;
      const creditLine = await getCreditLineByProduct(
        dispatch,
        businessIdentifier,
        'sgr',
      );
      setCreditLineData(creditLine);
    } catch (error) {
      console.error('Error fetching credit line:', error);
    } finally {
      setIsLoadingCreditLine(false);
    }
  };

  useEffect(() => {
    if (open && warranty) {
      fetchCreditLineData();
    }
  }, [open, warranty]);

  if (!warranty) return null;

  const hasCreditLineForType = () => {
    if (!creditLineData) return false;
    const subProduct = creditLineData.subProducts?.find(
      (sp: any) => sp.subProductId === warranty.type,
    );
    return subProduct && creditLineData.status === 'ACTIVE';
  };

  const canRequestWarranty = hasCreditLineForType();
  const warrantyType = SGR_TYPES.find(
    (type) => type.value === warranty.type,
  )?.label;

  if (isLoadingCreditLine) return <CenteredCircularProgress />;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        {!canRequestWarranty && warranty.status === 'QUOTED' && (
          <Alert
            severity="warning"
            icon={<Warning />}
            sx={{
              borderRadius: 2,
              mb: 3,
              '& .MuiAlert-message': {
                fontSize: '0.9rem',
              },
            }}
          >
            <Typography variant="subtitle2" fontWeight="medium" mb={1}>
              Línea de Crédito Requerida
            </Typography>
            <Typography variant="body2">
              No tienes línea de crédito activa para el tipo de garantía &quot;
              {warrantyType}&quot;. Contacta a tu ejecutivo para solicitar una
              línea de crédito.
            </Typography>
          </Alert>
        )}

        <WarrantyDetailTabs warranty={warranty} />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            flexDirection: 'row',
            gap: 2,
            mt: 3,
          }}
        >
          {warranty.status === 'QUOTED' && (
            <>
              <CustomButton color="error" onClick={onDelete}>
                Eliminar Cotización
              </CustomButton>
              <CustomButton
                color="primary"
                onClick={onRequest}
                disabled={!canRequestWarranty}
              >
                Solicitar Garantía
              </CustomButton>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WarrantyDetailDialog;
